<template>
  <div id="app" class="min-h-screen bg-white dark:bg-graphite transition-colors duration-200">
    <nav class="px-4 lg:px-6 h-16 flex items-center border-b border-primary-dark bg-primary sticky top-0 z-50">
      <!-- Logo -->
      <div class="flex items-center space-x-2 sm:space-x-4 flex-shrink-0 min-w-0">
        <router-link to="/" class="flex items-center justify-center min-w-0">
          <MountainIcon class="h-5 w-5 sm:h-6 sm:w-6 text-graphite flex-shrink-0" />
          <span class="ml-1 sm:ml-2 text-sm sm:text-base md:text-xl font-heading font-bold text-graphite whitespace-nowrap overflow-hidden">
            <span class="hidden sm:inline">Zero To Ultracycling</span>
            <span class="sm:hidden">Zero To Ultra</span>
          </span>
        </router-link>
      </div>

      <!-- Desktop Navigation -->
      <div class="hidden lg:flex ml-auto items-center space-x-4">
        <router-link to="/" class="text-sm font-body font-medium text-graphite hover:text-graphite/80 transition-colors">
          {{ $t('nav.home') }}
        </router-link>
        <router-link to="/blog" class="text-sm font-body font-medium text-graphite hover:text-graphite/80 transition-colors">
          {{ $t('nav.travels') }}
        </router-link>
        <router-link to="/blog" class="text-sm font-body font-medium text-graphite hover:text-graphite/80 transition-colors">
          {{ $t('nav.events') }}
        </router-link>
        <router-link to="/blog" class="text-sm font-body font-medium text-graphite hover:text-graphite/80 transition-colors">
          {{ $t('nav.routes') }}
        </router-link>
        <a 
          href="/#about" 
          class="text-sm font-body font-medium text-graphite hover:text-graphite/80 transition-colors"
          @click.prevent="navigateToAbout"
        >
          {{ $t('nav.about') }}
        </a>
        <LanguageSelector />
        <DarkModeToggle />
      </div>

      <!-- Mobile: Controls + Hamburger -->
      <div class="lg:hidden ml-auto flex items-center space-x-2">
        <LanguageSelector />
        <DarkModeToggle />
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="p-2 rounded-lg transition-colors duration-200 hover:bg-primary-dark"
          :aria-label="mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'"
        >
          <MenuIcon v-if="!mobileMenuOpen" class="h-6 w-6 text-graphite" />
          <XIcon v-else class="h-6 w-6 text-graphite" />
        </button>
      </div>
    </nav>

    <!-- Mobile Menu Overlay -->
    <transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="mobileMenuOpen"
        class="lg:hidden fixed inset-0 top-16 bg-black/50 z-30"
        @click="mobileMenuOpen = false"
      ></div>
    </transition>

    <!-- Mobile Menu -->
    <transition
      enter-active-class="transition-transform duration-300"
      enter-from-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-300"
      leave-from-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <div
        v-if="mobileMenuOpen"
        class="lg:hidden fixed inset-y-0 left-0 top-16 w-64 bg-primary z-40 overflow-y-auto shadow-xl"
      >
        <div class="px-4 py-6 space-y-2">
          <router-link
            to="/"
            @click="mobileMenuOpen = false"
            class="block py-3 px-4 text-base font-body font-medium text-graphite hover:bg-primary-dark rounded-lg transition-colors"
          >
            {{ $t('nav.home') }}
          </router-link>
          <router-link
            to="/blog"
            @click="mobileMenuOpen = false"
            class="block py-3 px-4 text-base font-body font-medium text-graphite hover:bg-primary-dark rounded-lg transition-colors"
          >
            {{ $t('nav.travels') }}
          </router-link>
          <router-link
            to="/blog"
            @click="mobileMenuOpen = false"
            class="block py-3 px-4 text-base font-body font-medium text-graphite hover:bg-primary-dark rounded-lg transition-colors"
          >
            {{ $t('nav.events') }}
          </router-link>
          <router-link
            to="/blog"
            @click="mobileMenuOpen = false"
            class="block py-3 px-4 text-base font-body font-medium text-graphite hover:bg-primary-dark rounded-lg transition-colors"
          >
            {{ $t('nav.routes') }}
          </router-link>
          <a
            href="/#about"
            @click.prevent="handleMobileAboutClick"
            class="block py-3 px-4 text-base font-body font-medium text-graphite hover:bg-primary-dark rounded-lg transition-colors"
          >
            {{ $t('nav.about') }}
          </a>
        </div>
      </div>
    </transition>

    <router-view/>
  </div>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router'
import { onMounted, watch, nextTick, ref } from 'vue'
import { Mountain as MountainIcon, Menu as MenuIcon, X as XIcon } from 'lucide-vue-next'
import DarkModeToggle from './components/DarkModeToggle.vue'
import LanguageSelector from './components/LanguageSelector.vue'

const router = useRouter()
const route = useRoute()
const mobileMenuOpen = ref(false)

const navigateToAbout = () => {
  if (route.path === '/') {
    // Si ya estamos en la home, solo hacer scroll
    const aboutSection = document.getElementById('about')
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' })
    }
  } else {
    // Si estamos en otra página, navegar a home y luego hacer scroll
    router.push('/').then(() => {
      nextTick(() => {
        const aboutSection = document.getElementById('about')
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: 'smooth' })
        }
      })
    })
  }
}

const handleMobileAboutClick = () => {
  mobileMenuOpen.value = false
  navigateToAbout()
}

// Cerrar menú móvil al cambiar de ruta
watch(() => route.path, () => {
  mobileMenuOpen.value = false
})

// Prevenir scroll del body cuando el menú móvil está abierto
watch(mobileMenuOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Cerrar menú móvil con tecla Escape
onMounted(() => {
  const handleEscape = (event) => {
    if (event.key === 'Escape' && mobileMenuOpen.value) {
      mobileMenuOpen.value = false
    }
  }
  document.addEventListener('keydown', handleEscape)
  
  return () => {
    document.removeEventListener('keydown', handleEscape)
    document.body.style.overflow = ''
  }
})

// Manejar scroll cuando se navega con hash
watch(() => route.hash, (hash) => {
  if (hash === '#about' && route.path === '/') {
    nextTick(() => {
      const aboutSection = document.getElementById('about')
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth' })
      }
    })
  }
})

// Manejar scroll al cargar la página con hash
onMounted(() => {
  if (route.hash === '#about' && route.path === '/') {
    nextTick(() => {
      const aboutSection = document.getElementById('about')
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth' })
      }
    })
  }
})
</script>

<style>
#app {
  font-family: 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #1B1B1B;
  margin-top: 0;
}

/* Transiciones suaves para el cambio de modo */
* {
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}
</style>
